import * as React from 'react'
import Dialog from '@material-ui/core/Dialog'
import { useAtom } from 'jotai'
import styled from 'styled-components'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { Input, Textarea } from '@geist-ui/react'
import { Twitter, CheckInCircleFill, ChevronLeft } from '@geist-ui/react-icons'

import {
  isEditProfileOpenAtom,
  profileVerificationProgressAtom,
  ProfileVerificationProgressState,
  wUpdateProfileAtom,
} from '../../../atoms/profile'
import { ProfileMetadata } from '../../../interfaces'
import BannerUpload from './BannerUpload'
import AvatarUpload from './AvatarUpload'
import { db } from '../../../data/FirebaseUtils'
import { spacebarListenerActiveAtom } from '../../video/videoplayer/atoms'

// ========================== React component ==================================

interface Props {
  address: string
  metadata?: ProfileMetadata
}

const ProfileEditDialog = ({ metadata, address }: Props): JSX.Element => {
  const [isEditProfileOpen, setEditProfileOpen] = useAtom(isEditProfileOpenAtom)
  const [, updateProfile] = useAtom(wUpdateProfileAtom)
  const [, setSpaceBarActive] = useAtom(spacebarListenerActiveAtom)

  const [name, setName] = React.useState<string | undefined>(undefined)
  const [bio, setBio] = React.useState<string | undefined>(undefined)
  const [profilePic, setProfilePic] = React.useState<File | undefined>(undefined)
  const [banner, setBanner] = React.useState<File | undefined>(undefined)
  const [youtube, setYoutube] = React.useState<string | undefined>(undefined)
  const [instagram, setInstagram] = React.useState<string | undefined>(undefined)
  const [saving, setSaving] = React.useState(false)

  // Regarding twitter verification.
  const [verificationProgress, setProgressState] = useAtom(profileVerificationProgressAtom)
  const isVerified = metadata?.twitter_link !== undefined

  const fullscreen = useMediaQuery('(max-width: 43em)')
  const isTablet = useMediaQuery('(max-width: 62em)')

  const update = async (): Promise<void> => {
    setSaving(true)
    const userDoc = (await db.users.doc(address).get()).data()
    let formattedIG = undefined
    if (instagram) {
      formattedIG = instagram.replace('https://www.instagram.com/', '')
    }
    let formattedYT = undefined
    if (youtube) {
      formattedYT = youtube.replace('https://www.youtube.com/', '')
    }
    updateProfile({
      name,
      bio,
      profilePic,
      banner,
      youtube: formattedYT || youtube,
      instagram: formattedIG || instagram,
      twitter: userDoc?.twitter_link,
    })
  }

  React.useEffect(() => {
    if (!isEditProfileOpen) {
      setSpaceBarActive(true)
      setSaving(false)
      setName(undefined)
      setBio(undefined)
      setProfilePic(undefined)
      setBanner(undefined)
      setYoutube(undefined)
      setInstagram(undefined)
    }
  }, [isEditProfileOpen])

  return (
    <Dialog
      aria-labelledby="profile-edit-dialog"
      open={isEditProfileOpen}
      fullScreen={fullscreen}
      onClose={(): void => setEditProfileOpen(false)}
      PaperProps={{
        style: {
          backgroundColor: '#101010',
          borderRadius: fullscreen ? 0 : 30,
          minWidth: isTablet ? '75vw' : '50vw',
          minHeight: isTablet ? '75vw' : '75vh',
          height: !fullscreen ? (isTablet ? '75vw' : '75vh') : '',
        },
      }}
      BackdropProps={{
        style: {
          backdropFilter: 'blur(20px)',
        },
      }}
    >
      <OuterDiv>
        <NavigationHeaderRow>
          <LeftHeader>
            <CloseButton color="white" size={'35px'} onClick={() => setEditProfileOpen(false)} />
            <Title>Edit Profile</Title>
          </LeftHeader>
          <SaveAndCloseButton disabled={saving} onClick={update}>
            {saving ? 'Saving...' : 'Save'}
          </SaveAndCloseButton>
        </NavigationHeaderRow>
        <BannerUpload value={banner} onChange={setBanner} arweave={metadata?.banner_image_uri} />
        <AvatarUpload
          value={profilePic}
          onChange={setProfilePic}
          arweave={metadata?.profile_image_uri}
        />
        <TextfieldsColumn>
          <Header>Name</Header>
          <Textfield
            placeholder={'...'}
            initialValue={name ? name : metadata?.display_name ? metadata.display_name : undefined}
            enterKeyHint={undefined}
            width="100%"
            scale={4 / 3}
            onChange={(e) => {
              setName(e.target.value)
            }}
          />
          <LittleSpacer />
          <Header>Bio</Header>
          <LargeTextfield
            placeholder={'...'}
            initialValue={bio ? bio : metadata?.bio ? metadata.bio : undefined}
            width="100%"
            scale={4 / 3}
            onChange={(e) => {
              setBio(e.target.value)
            }}
          />
          <LittleSpacer />
          <Header>Twitter</Header>
          {!isVerified && verificationProgress !== ProfileVerificationProgressState.SUCCESS && (
            <VerifyButtonRow
              onClick={() => {
                setProgressState(ProfileVerificationProgressState.SIGN_MESSAGE)
              }}
            >
              <Twitter color="white" />
              <VerifyButtonTitle>Verify Twitter</VerifyButtonTitle>
            </VerifyButtonRow>
          )}
          {(isVerified || verificationProgress === ProfileVerificationProgressState.SUCCESS) && (
            <VerifiedRow>
              <CheckInCircleFill color="white" />
              <VerifyButtonTitle color="white">Verified!</VerifyButtonTitle>
            </VerifiedRow>
          )}
          <LittleSpacer />
          <Header>YouTube</Header>
          <Textfield
            placeholder={'e.g. MrBeast6000'}
            initialValue={
              youtube ? youtube : metadata?.youtube_link ? metadata.youtube_link : undefined
            }
            enterKeyHint={undefined}
            width="100%"
            scale={4 / 3}
            onChange={(e) => {
              setYoutube(e.target.value)
            }}
          />
          <LittleSpacer />
          <Header>Instagram</Header>
          <Textfield
            placeholder={'e.g. peter_pan'}
            initialValue={
              instagram ? instagram : metadata?.instagram_link ? metadata.instagram_link : undefined
            }
            enterKeyHint={undefined}
            width="100%"
            scale={4 / 3}
            onChange={(e) => {
              setInstagram(e.target.value)
            }}
          />
        </TextfieldsColumn>
      </OuterDiv>
    </Dialog>
  )
}

export default ProfileEditDialog

// ========================== Styled components ================================

const Title = styled.div`
  color: white;
  font-size: 28px;
  font-weight: 700;
  margin-left: 24px;

  // tablet
  @media (max-width: 62em) {
    font-size: 28px;
  }

  // mobile
  @media (max-width: 43em) {
    font-size: 20px;
  }
`

const LeftHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`

const NavigationHeaderRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: 20px;
`

const VerifiedRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
`

const VerifyButtonRow = styled.div`
  display: flex;
  flex-direction: row;
  cursor: pointer;
  gap: 10px;
  padding: 5px 20px 5px 20px;
  background: rgb(27, 27, 27);
  margin-right: auto;
  border-radius: 15px;
`

const VerifyButtonTitle = styled.div`
  font-weight: 500;
  color: white;
  font-size: 16px;
`

const TextfieldsColumn = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-top: -65px;
`

const OuterDiv = styled.div`
  padding: 30px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`

const Header = styled.div`
  color: white;
  font-size: 18px;
  font-weight: 400;
  margin-right: auto;
  margin-bottom: 10px;
`

const LittleSpacer = styled.div`
  height: 25px;
`

const SaveAndCloseButton = styled.div<{ disabled: boolean }>`
  background-color: ${(props) => (props.disabled ? 'grey' : 'white')};
  pointer-events: ${(props) => (props.disabled ? 'none' : 'all')};
  color: black;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 14px;
  border-radius: 14px;
  padding: 5px 23px 5px 23px;

  @media (min-width: 43em) {
    font-size: 18px;
    border-radius: 14px;
  }
`

const Textfield = styled(Input)``

const LargeTextfield = styled(Textarea)``

const CloseButton = styled(ChevronLeft)`
  :hover {
    cursor: pointer;
  }
`
